import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Building2, MapPin, Phone, Mail, Globe, Clock, DollarSign, X } from 'lucide-react';

/**
 * Composant d'autocomplétion pour sélectionner un fournisseur
 * Affiche une liste déroulante qui se filtre en temps réel
 * Remplit automatiquement tous les champs liés au fournisseur
 */
const SupplierAutocomplete = ({ 
  suppliers = [],
  value = {},
  onChange,
  placeholder = "Tapez pour rechercher un fournisseur...",
  onClear
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Filtrer les fournisseurs selon la recherche
  const filteredSuppliers = suppliers.filter(supplier => {
    if (!query.trim()) return true;
    const search = query.toLowerCase();
    return (
      supplier.name?.toLowerCase().includes(search) ||
      supplier.country?.toLowerCase().includes(search) ||
      supplier.contacts?.some(c => 
        c.name?.toLowerCase().includes(search) ||
        c.email?.toLowerCase().includes(search)
      )
    );
  });

  // Fermer le dropdown si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Gestion du clavier
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredSuppliers.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredSuppliers[highlightedIndex]) {
          handleSelect(filteredSuppliers[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  // Sélection d'un fournisseur
  const handleSelect = (supplier) => {
    // Extraire le premier contact et la première adresse pour simplifier
    const primaryContact = supplier.contacts?.[0] || {};
    const primaryAddress = supplier.addresses?.[0] || {};

    // Construire l'objet fournisseur avec toutes les infos
    const selectedSupplier = {
      name: supplier.name || '',
      url: supplier.url || '',
      country: supplier.country || 'France',
      deliveryTime: supplier.delivery_time || '',
      paymentMethod: supplier.payment_method || '',
      lastPrice: 0, // Prix à définir manuellement
      contactName: primaryContact.name || '',
      phone: primaryContact.phone || '',
      email: primaryContact.email || '',
      address: primaryAddress.street ? 
        `${primaryAddress.street}, ${primaryAddress.postalCode || ''} ${primaryAddress.city || ''}`.trim() : 
        ''
    };

    onChange(selectedSupplier);
    setQuery(supplier.name);
    setIsOpen(false);
  };

  // Effacer la sélection
  const handleClear = () => {
    setQuery('');
    if (onClear) {
      onClear();
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      {/* Input de recherche */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setHighlightedIndex(0);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full bg-white dark:bg-slate-900 p-2 pr-20 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-xs focus:ring-2 focus:ring-blue-500"
        />
        
        {/* Icônes à droite */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {query && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
            >
              <X className="w-3 h-3 text-slate-400" />
            </button>
          )}
          <ChevronDown 
            className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {/* Dropdown des suggestions */}
      {isOpen && filteredSuppliers.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-xl shadow-2xl max-h-[400px] overflow-y-auto">
          {filteredSuppliers.map((supplier, index) => {
            const primaryContact = supplier.contacts?.[0] || {};
            const primaryAddress = supplier.addresses?.[0] || {};
            
            return (
              <button
                key={supplier.id || index}
                type="button"
                onClick={() => handleSelect(supplier)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`w-full text-left p-4 border-b dark:border-slate-700 last:border-0 transition-all ${
                  index === highlightedIndex 
                    ? 'bg-blue-50 dark:bg-blue-900/20' 
                    : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                }`}
              >
                {/* Nom du fournisseur */}
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4 text-blue-600" />
                  <span className="font-bold text-sm dark:text-white">
                    {supplier.name}
                  </span>
                  {supplier.country && (
                    <span className="text-[10px] px-2 py-0.5 bg-slate-200 dark:bg-slate-700 rounded-full text-slate-600 dark:text-slate-300">
                      {supplier.country}
                    </span>
                  )}
                </div>

                {/* Informations complémentaires en grille */}
                <div className="grid grid-cols-2 gap-2 text-[10px] text-slate-500 dark:text-slate-400">
                  {primaryContact.name && (
                    <div className="flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      <span className="truncate">{primaryContact.name}</span>
                    </div>
                  )}
                  {primaryContact.email && (
                    <div className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      <span className="truncate">{primaryContact.email}</span>
                    </div>
                  )}
                  {supplier.delivery_time && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>{supplier.delivery_time}</span>
                    </div>
                  )}
                  {supplier.payment_method && (
                    <div className="flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      <span className="truncate">{supplier.payment_method}</span>
                    </div>
                  )}
                  {primaryAddress.street && (
                    <div className="flex items-center gap-1 col-span-2">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">
                        {primaryAddress.street}, {primaryAddress.city}
                      </span>
                    </div>
                  )}
                  {supplier.url && (
                    <div className="flex items-center gap-1 col-span-2">
                      <Globe className="w-3 h-3" />
                      <span className="truncate text-blue-600 dark:text-blue-400">
                        {supplier.url}
                      </span>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Message si aucun résultat */}
      {isOpen && query && filteredSuppliers.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-xl shadow-2xl p-4 text-center">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Aucun fournisseur trouvé pour "{query}"
          </p>
          <button
            type="button"
            onClick={() => {
              setQuery('');
              setIsOpen(false);
            }}
            className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            Effacer la recherche
          </button>
        </div>
      )}
    </div>
  );
};

export default SupplierAutocomplete;
